import {
    FASTSHIFT_RUNBOOK_STEP_STATUS,
    FastshiftRunbookStep
} from "@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types";
import { FormattedMessage, useIntl } from "react-intl";

import Badge from "@atlaskit/badge";
import Heading from "@atlaskit/heading";
import CheckCircleIcon from "@atlaskit/icon/core/check-circle";
import FlagFilledIcon from "@atlaskit/icon/core/flag-filled";
import ProblemIcon from "@atlaskit/icon/core/problem";
import { Box, Inline, Stack, Text, xcss } from "@atlaskit/primitives-next";
import { token } from "@atlaskit/tokens";
import Tooltip from "@atlaskit/tooltip";

import { messages } from "./messages";
import { RunbookResponsibleMetadata } from "./runbook-metadata";

const phaseGroupStyles = xcss({
    borderInlineStart: `2px solid ${token("color.border")}`,
    paddingBlockStart: "space.100",
    paddingInlineStart: "space.150"
});

const phaseHeaderStyles = xcss({
    borderBlockEnd: `1px solid ${token("color.border")}`,
    paddingBlockEnd: "space.075",
    paddingInline: "space.100"
});

const stepRowStyles = xcss({
    paddingBlock: "space.100",
    paddingInline: "space.100"
});

const disabledStepRowStyles = xcss({
    paddingBlock: "space.100",
    paddingInline: "space.100",
    backgroundColor: "color.background.disabled"
});

const separatedStepRowStyles = xcss({
    borderBlockStart: `1px solid ${token("color.border.disabled")}`
});

const completedStepTitleStyles = xcss({
    textDecoration: "line-through",
    color: "color.text.subtle"
});

const stepBadgeStyles = xcss({
    flexShrink: 0
});

const stepTitleStyles = xcss({
    minWidth: "0"
});

const stepIconContainerStyles = xcss({
    flexShrink: 0
});

type RunbookStepPhaseGroup = {
    phase: string;
    steps: FastshiftRunbookStep[];
};

const getStepPhase = (step: FastshiftRunbookStep, fallbackPhase: string) => {
    const phase = step.phase.trim();

    return phase || fallbackPhase;
};

const groupStepsByPhase = (
    steps: FastshiftRunbookStep[],
    fallbackPhase: string
): RunbookStepPhaseGroup[] => {
    const sortedSteps = [...steps].sort(
        (firstStep, secondStep) => firstStep.stepOrder - secondStep.stepOrder
    );

    return sortedSteps.reduce<RunbookStepPhaseGroup[]>((groups, step) => {
        const phase = getStepPhase(step, fallbackPhase);
        const existingGroup = groups.find((group) => group.phase === phase);

        if (existingGroup) {
            existingGroup.steps.push(step);
        } else {
            groups.push({ phase, steps: [step] });
        }

        return groups;
    }, []);
};

const RunbookStepStatusIcons = ({ step }: { step: FastshiftRunbookStep }) => {
    const intl = useIntl();
    const isStepDone = step.status === FASTSHIFT_RUNBOOK_STEP_STATUS.DONE;

    if (!step.disabled && !step.risk && !isStepDone) {
        return null;
    }

    const disabledLabel = intl.formatMessage(
        messages.runbooksPanelStepDisabledIconLabel
    );
    const riskLabel = intl.formatMessage(
        messages.runbooksPanelStepRiskIconLabel
    );
    const doneLabel = intl.formatMessage(
        messages.runbooksPanelStepDoneIconLabel
    );

    return (
        <Inline
            alignBlock="start"
            alignInline="end"
            space="space.075"
            xcss={stepIconContainerStyles}
        >
            {step.disabled && (
                <Tooltip content={disabledLabel}>
                    <ProblemIcon
                        label={disabledLabel}
                        color={token("color.icon.subtle")}
                    />
                </Tooltip>
            )}
            {step.risk && (
                <Tooltip content={riskLabel}>
                    <FlagFilledIcon
                        label={riskLabel}
                        color={token("color.icon.danger")}
                    />
                </Tooltip>
            )}
            {isStepDone && (
                <Tooltip content={doneLabel}>
                    <CheckCircleIcon
                        label={doneLabel}
                        color={token("color.icon.success")}
                    />
                </Tooltip>
            )}
        </Inline>
    );
};

const RunbookStepRow = ({
    step,
    hasDivider
}: {
    step: FastshiftRunbookStep;
    hasDivider: boolean;
}) => {
    const isStepDone = step.status === FASTSHIFT_RUNBOOK_STEP_STATUS.DONE;

    return (
        <Box
            xcss={[
                step.disabled ? disabledStepRowStyles : stepRowStyles,
                hasDivider && separatedStepRowStyles
            ]}
        >
            <Stack space="space.100">
                <Inline
                    alignBlock="start"
                    spread="space-between"
                    space="space.100"
                >
                    <Inline alignBlock="start" space="space.075" grow="fill">
                        <Box xcss={stepBadgeStyles}>
                            <Badge>{step.stepOrder}</Badge>
                        </Box>
                        <Box
                            xcss={
                                isStepDone
                                    ? completedStepTitleStyles
                                    : stepTitleStyles
                            }
                        >
                            <Text weight="medium">{step.title}</Text>
                        </Box>
                    </Inline>
                    <RunbookStepStatusIcons step={step} />
                </Inline>
                {step.responsible && (
                    <RunbookResponsibleMetadata value={step.responsible} />
                )}
            </Stack>
        </Box>
    );
};

const RunbookPhaseGroup = ({ group }: { group: RunbookStepPhaseGroup }) => (
    <Stack space="space.050" xcss={phaseGroupStyles}>
        <Box xcss={phaseHeaderStyles}>
            <Text size="small" weight="semibold">
                {group.phase}
            </Text>
        </Box>
        <Stack space="space.100">
            {group.steps.map((step, index) => (
                <RunbookStepRow
                    key={step.stepId}
                    step={step}
                    hasDivider={index > 0}
                />
            ))}
        </Stack>
    </Stack>
);

export const RunbookStepList = ({
    steps
}: {
    steps: FastshiftRunbookStep[];
}) => {
    const intl = useIntl();
    const groupedSteps = groupStepsByPhase(
        steps,
        intl.formatMessage(messages.runbooksPanelOtherPhaseLabel)
    );

    return (
        <Stack space="space.100">
            <Heading size="xsmall" as="h3">
                <FormattedMessage {...messages.runbooksPanelStepsTitle} />
            </Heading>
            <Stack space="space.200">
                {groupedSteps.map((group) => (
                    <RunbookPhaseGroup key={group.phase} group={group} />
                ))}
            </Stack>
        </Stack>
    );
};
